import { apiPath } from '../../constants';
import {
    axios,
    apiRequestOptions,
    errorRequestHandler,
    transformHttpResponse,
} from 'utils';

export const DRAW_SWAP_REQUEST = 'POST_DRAW_SWAP_REQUEST';
export const DRAW_SWAP_FAILURE = 'POST_DRAW_SWAP_FAILURE';
export const DRAW_SWAP_SUCCESS = 'POST_DRAW_SWAP_SUCCESS';

export const DRAW_RESET_REQUEST = 'POST_DRAW_RESET_REQUEST';
export const DRAW_RESET_FAILURE = 'POST_DRAW_RESET_FAILURE';
export const DRAW_RESET_SUCCESS = 'POST_DRAW_RESET_SUCCESS';

export const DRAW_APPROVE_REQUEST = 'POST_DRAW_APPROVE_REQUEST';
export const DRAW_APPROVE_FAILURE = 'POST_DRAW_APPROVE_FAILURE';
export const DRAW_APPROVE_SUCCESS = 'POST_DRAW_APPROVE_SUCCESS';

export const DRAW_REOPEN_REQUEST = 'POST_DRAW_REOPEN_REQUEST';
export const DRAW_REOPEN_FAILURE = 'POST_DRAW_REOPEN_FAILURE';
export const DRAW_REOPEN_SUCCESS = 'POST_DRAW_REOPEN_SUCCESS';

export const DRAW_CHANGES_REQUEST = 'FETCH_DRAW_CHANGES_REQUEST';
export const DRAW_CHANGES_FAILURE = 'FETCH_DRAW_CHANGES_FAILURE';
export const DRAW_CHANGES_SUCCESS = 'FETCH_DRAW_CHANGES_SUCCESS';

/**
 * Draw Management Workflow (backend = single source of truth, every
 * operation auto-saves the Draft by definition — it IS a server write):
 *
 *  - swapDrawAthletes: swap two occupied first-round positions. `operation`
 *    tags the audit trail ('swap' | 'undo' | 'redo') — an undo is the same
 *    swap re-applied (a swap is its own inverse). Resolves to the updated
 *    rows on success (undefined on failure) so the module can maintain its
 *    undo/redo history only for operations that really persisted.
 *  - resetDraw: restore exactly the generated draw.
 *  - approveDraw / reopenDraw: lifecycle transitions.
 *  - fetchDrawChanges: the manual-change report (diff vs generated draw).
 *
 * Every success drops the category's cached overview (reducer) so the
 * status chip / indicators refetch fresh.
 */
export const swapDrawAthletes = (competition, category, positionA, positionB, operation = 'swap') => {
    return function(dispatch) {
        dispatch({ type: DRAW_SWAP_REQUEST });
        return axios(apiRequestOptions(
            apiPath.DRAW_WORKFLOW.SWAP,
            { positionA, positionB, operation },
            { params: { competition, category } }
        ))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: DRAW_SWAP_SUCCESS, results: response.data, competition, category, operation });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: DRAW_SWAP_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

export const resetDraw = (competition, category) => {
    return function(dispatch) {
        dispatch({ type: DRAW_RESET_REQUEST });
        return axios(apiRequestOptions(apiPath.DRAW_WORKFLOW.RESET, null, { params: { competition, category } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: DRAW_RESET_SUCCESS, results: response.data, competition, category });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: DRAW_RESET_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

export const approveDraw = (competition, category) => {
    return function(dispatch) {
        dispatch({ type: DRAW_APPROVE_REQUEST });
        return axios(apiRequestOptions(apiPath.DRAW_WORKFLOW.APPROVE, null, { params: { competition, category } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: DRAW_APPROVE_SUCCESS, competition, category });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: DRAW_APPROVE_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

/**
 * «Сітку роздруковано» ticked or unticked (17 Oct 2026) — held for the draw
 * it was ticked on. The caller re-reads the category list, whose row says so.
 */
export const markDrawPrinted = (competition, category, printed) => dispatch =>
    axios(apiRequestOptions(apiPath.DRAW_WORKFLOW.PRINTED, { printed }, { params: { competition, category } }))
        .then(transformHttpResponse)
        .then(response => response.data)
        .catch(error => transformHttpResponse(error.response))
        .catch(error => {
            dispatch(errorRequestHandler(error));
        });

/**
 * Reopen a confirmed OR locked bracket (super-admin only). A CONFIRMED draw
 * (no results yet) reopens directly; a LOCKED bracket (competition started)
 * needs `force: true` and a MANDATORY reason — the backend then clears every
 * recorded result and returns the bracket to draft, fully audited.
 */
export const reopenDraw = (competition, category, { reason = null, force = false } = {}) => {
    return function(dispatch) {
        dispatch({ type: DRAW_REOPEN_REQUEST });
        return axios(apiRequestOptions(apiPath.DRAW_WORKFLOW.REOPEN, { reason, force }, { params: { competition, category } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: DRAW_REOPEN_SUCCESS, competition, category });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: DRAW_REOPEN_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

export const fetchDrawChanges = (competition, category) => {
    return function(dispatch) {
        dispatch({ type: DRAW_CHANGES_REQUEST, competition, category });
        return axios(apiRequestOptions(apiPath.DRAW_WORKFLOW.CHANGES, null, { params: { competition, category } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: DRAW_CHANGES_SUCCESS, competition, category, data: response.data });
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: DRAW_CHANGES_FAILURE, competition, category, error });
                dispatch(errorRequestHandler(error));
            });
    }
};
